import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslate, useGetList, useGetOne } from "react-admin";
import {
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Autocomplete,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles/index.js";
import Config from "../../config.js";

const SendNotification = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const pushID = localStorage.getItem("pushNotificationServiceId");

  const t = useTranslate();
  const { data: clientsData } = useGetList("Client", {
    filter: { status: "confirmed" },
    pagination: { perPage: 10000 },
  });

  const { data: categoriesData } = useGetList("Category");
  const { data: tagsData } = useGetList("Tag");
  const { data: filesData } = useGetList("File");

  const { data: pushNotificationServicesData } = useGetOne(
    "PushNotificationService",
    { id: pushID }
  );

  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [pushNotificationService, setPushNotificationService] = useState({});
  const [header, setHeader] = useState("");
  const [message, setMessage] = useState("");
  const [previewImage, setPreviewImage] = useState("");
  const [recipientType, setRecipientType] = useState("all");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    vertical: "bottom",
    horizontal: "center",
  });

  useEffect(() => {
    if (clientsData && clientsData.length) {
      setClients(clientsData);
    }
    if (categoriesData && categoriesData.length) {
      setCategories(categoriesData);
    }
    if (tagsData && tagsData.length) {
      setTags(tagsData);
    }
    if (pushNotificationServicesData) {
      setPushNotificationService(pushNotificationServicesData);
    }
  }, [clientsData, categoriesData, pushNotificationServicesData]);

  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  const handleSendPushNotification = async (e) => {
    e.preventDefault();

    setShowFieldErrors(true);

    if (!header.trim() || !message.trim()) {
      return;
    }
    if (!header.trim() || !message.trim()) {
      return;
    }

    if (recipientType === "categories" && !selectedCategory) {
      setSnackbar({
        open: true,
        message: t(
          "resources.PushNotificationService.source.errorSelectCategory"
        ),
        severity: "error",
        vertical: "bottom",
        horizontal: "center",
      });
      return;
    }

    if (recipientType === "tags" && selectedTags.length === 0) {
      setSnackbar({
        open: true,
        message: t("resources.PushNotificationService.source.errorSelectTag"),
        severity: "error",
        vertical: "bottom",
        horizontal: "center",
      });
      return;
    }

    if (recipientType === "single" && !selectedUser) {
      setSnackbar({
        open: true,
        message: t("resources.PushNotificationService.source.errorSelectUser"),
        severity: "error",
        vertical: "bottom",
        horizontal: "center",
      });
      return;
    }

    if (!pushNotificationService || !clients?.length) {
      setSnackbar({
        open: true,
        message: "Сервіс push-сповіщень або клієнти недоступні",
        severity: "error",
        vertical: "bottom",
        horizontal: "center",
      });
      return;
    }

    setShowFieldErrors(false);

    if (header && message && pushNotificationService) {
      try {
        let targetClients = clients;

        if (recipientType === "active") {
          targetClients = clients.filter((client) => client.isActive);
        } else if (recipientType === "single" && selectedUser) {
          targetClients = clients.filter(
            (client) => client.id === selectedUser
          );
        } else if (recipientType === "categories" && selectedCategory) {
          targetClients = targetClients.filter(
            (client) => client.category_id === selectedCategory
          );
        } else if (recipientType === "tags" && selectedTags.length) {
          const selectedTagIds = selectedTags.map((tag) => tag.id);

          targetClients = targetClients.filter((client) =>
            client.tag_ids?.some((tagId) => selectedTagIds.includes(tagId))
          );
        }

        const imageUrl = previewImage || "";

        const notification = {
          push_notification_service_id: pushNotificationService.id,
          app_id: pushNotificationService.config.app_id,
          contents: { en: message, uk: message },
          headings: { en: header, uk: header },
          mutable_content: true,
          ios_attachments: imageUrl,
          big_picture: imageUrl,
          include_aliases:
            recipientType === "single"
              ? { external_id: [selectedUser] }
              : { external_id: targetClients.map((client) => client.id) },
          target_channel: "push",
          priority: 10,
          tags: selectedTags.map((tag) => tag.id),
          api_key: pushNotificationService.config.token,
          recipient_type: recipientType,
        };

        const response = await axios.post(
          `${Config.serverUrl}api/merchant/send_push_notification`,
          notification,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        handleSuccessResponse(response);
      } catch (error) {
        if (error.message.includes("status code 400")) {
          setSnackbar({
            open: true,
            message:
              t("resources.notifications.errors.Forbidden") ||
              "Помилка відправки повідомлення",
            severity: "error",
            vertical: "bottom",
            horizontal: "center",
          });
        } else {
          setSnackbar({
            open: true,
            message:
              t(`resources.notifications.errors.${error.message}`) ||
              "Помилка відправки повідомлення",
            severity: "error",
            vertical: "bottom",
            horizontal: "center",
          });
        }
      } finally {
        setSending(false);
      }
    }
  };

  const handleSuccessResponse = (response) => {
    console.log("Notification sent successfully:", response);

    setSnackbar({
      open: true,
      message: t(
        "resources.PushNotificationService.source.messageSentSuccessfully"
      ),
      severity: "success",
      vertical: "bottom",
      horizontal: "center",
    });

    setHeader("");
    setMessage("");
    setPreviewImage("");
    setRecipientType("all");
  };

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: "900px",
        margin: "20px auto",
        boxShadow: "0 3px 6px rgba(0,0,0,0.25)",
        borderRadius: "8px",

        padding: "20px",
      }}
    >
      <CardContent
        sx={{
          padding: "44px 55px",
          backgroundColor: isDarkMode
            ? "radial-gradient(circle,rgba(94, 87, 97, 1) 0%, rgba(38, 35, 64, 1) 100%)"
            : "#fff",
          position: "relative",
          zIndex: 10,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: "$dark-primary-color",
            fontSize: "1.625em",
            fontWeight: "bold",
            paddingBottom: "25px",
          }}
        >
          {t("resources.PushNotificationService.source.sendTitle")}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <form onSubmit={handleSendPushNotification}>
          <TextField
            label={t("resources.PushNotificationService.notificationHeader")}
            variant="outlined"
            fullWidth
            inputProps={{ maxLength: 64 }}
            value={header}
            onChange={(e) => {
              setHeader(e.target.value);
            }}
            error={!header.trim() && showFieldErrors}
            helperText={
              !header.trim() && showFieldErrors
                ? t("resources.notifications.errors.empty")
                : ""
            }
            margin="normal"
            sx={{
              "& .MuiOutlinedInput-root": {
                background: isDarkMode
                  ? "radial-gradient(circle,rgba(94, 87, 97, 1) 0%, rgba(38, 35, 64, 1) 100%)"
                  : "#fff",
                padding: "5px 5px",
                "&:hover": {
                  borderColor: "gray",
                },
                "&.Mui-focused": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "gray",
                  },
                  "& .MuiInputBase-input": {
                    color: isDarkMode ? "#fff" : "black",
                  },
                },
              },
              "& .MuiInputLabel-root": {
                color: isDarkMode ? "#fff" : "black",
              },
            }}
          />

          <TextField
            label={t("resources.PushNotificationService.notificationMessage")}
            variant="outlined"
            fullWidth
            inputProps={{ maxLength: 400 }}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            rows={6}
            error={!message.trim() && showFieldErrors}
            helperText={
              !message.trim() && showFieldErrors
                ? t("resources.notifications.errors.empty")
                : ""
            }
            margin="normal"
            sx={{
              "& .MuiOutlinedInput-root": {
                background: isDarkMode
                  ? "radial-gradient(circle,rgba(94, 87, 97, 1) 0%, rgba(38, 35, 64, 1) 100%)"
                  : "#fff",
                padding: "10px 15px",
                "&:hover": {
                  borderColor: "gray",
                },
                "&.Mui-focused": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "gray",
                  },
                  "& .MuiInputBase-input": {
                    color: isDarkMode ? "#fff" : "black",
                  },
                },
              },
              "& .MuiInputLabel-root": {
                color: isDarkMode ? "#fff" : "black",
              },
            }}
          />

          <Box sx={{ mt: 2, mb: 2 }}>
            <Autocomplete
              options={filesData}
              getOptionLabel={(option) => option.name}
              onChange={(event, file) => {
                if (file && file.id) {
                  setPreviewImage(file.url);
                } else {
                  setPreviewImage("");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    "resources.PushNotificationService.source.selectPhoto"
                  )}
                  variant="outlined"
                  fullWidth
                />
              )}
              sx={{ marginTop: 2, marginBottom: 2 }}
              freeSolo
            />

            {previewImage && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("resources.PushNotificationService.source.preview")}
                </Typography>
                <img
                  src={previewImage}
                  alt="Попередній перегляд"
                  style={{
                    maxWidth: "200px",
                    maxHeight: "200px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </Box>
            )}
          </Box>
          <FormControl component="fieldset">
            <Typography variant="subtitle1">
              {t("resources.PushNotificationService.source.toWhomToSend")}
            </Typography>
            <RadioGroup
              row
              value={recipientType}
              onChange={(e) => setRecipientType(e.target.value)}
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label={t("resources.PushNotificationService.source.allUsers")}
              />
              <FormControlLabel
                value="single"
                control={<Radio />}
                label={t("resources.PushNotificationService.source.singleUser")}
              />
              <FormControlLabel
                value="categories"
                control={<Radio />}
                label={t("resources.PushNotificationService.source.categories")}
              />
              <FormControlLabel
                value="tags"
                control={<Radio />}
                label={t("resources.PushNotificationService.source.tags")}
              />
            </RadioGroup>
          </FormControl>

          {recipientType === "single" && clients?.length > 0 && (
            <Autocomplete
              options={clients?.length && clients}
              getOptionLabel={(option) =>
                `${option.last_name} ${option.first_name} ${
                  option.middle_name || ""
                }`
              }
              getOptionKey={(value) => value.id}
              onChange={(event, newValue) => {
                if (newValue && newValue.id) {
                  setSelectedUser(newValue.id);
                } else {
                  setSelectedUser("");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    "resources.PushNotificationService.source.selectUser"
                  )}
                  variant="outlined"
                  fullWidth
                />
              )}
              sx={{ marginTop: 2, marginBottom: 2 }}
              slotProps={{
                paper: {
                  sx: {
                    background: isDarkMode
                      ? "linear-gradient(to right, #1f1c2c, #928dab)"
                      : "linear-gradient(279deg,rgba(208, 184, 227, 1) 0%, rgba(232, 232, 237, 1) 43%)",

                    color: isDarkMode ? "white" : "black",
                    borderRadius: "12px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
                    "& .MuiAutocomplete-option": {
                      padding: "10px 16px",
                    },
                  },
                },
              }}
              freeSolo
            />
          )}

          {recipientType === "categories" && categories?.length > 0 && (
            <Autocomplete
              options={categories}
              getOptionLabel={(option) => option.name || option.id.toString()}
              onChange={(event, newValue) => {
                if (newValue) {
                  setSelectedCategory(newValue.id || "");
                } else {
                  setSelectedCategory("");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    "resources.PushNotificationService.source.chooseCategory"
                  )}
                  variant="outlined"
                  fullWidth
                />
              )}
              sx={{ marginTop: 2, marginBottom: 2 }}
              slotProps={{
                paper: {
                  sx: {
                    background: isDarkMode
                      ? "linear-gradient(to right, #1f1c2c, #928dab)"
                      : "linear-gradient(279deg,rgba(208, 184, 227, 1) 0%, rgba(232, 232, 237, 1) 43%)",

                    color: isDarkMode ? "white" : "black",
                    // background: "linear-gradient(to right, #1f1c2c, #928dab);",

                    borderRadius: "12px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
                    "& .MuiAutocomplete-option": {},
                  },
                },
              }}
              freeSolo
            />
          )}
          {recipientType === "tags" && (
            <Autocomplete
              multiple
              id="tags-select"
              options={tags || []}
              getOptionLabel={(option) => option.name}
              value={selectedTags}
              onChange={(event, newValue) => setSelectedTags(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    "resources.PushNotificationService.source.selectTags"
                  )}
                  variant="outlined"
                  error={!selectedTags.length && showFieldErrors}
                  helperText={
                    !selectedTags.length && showFieldErrors
                      ? t("resources.notifications.errors.empty")
                      : ""
                  }
                />
              )}
              slotProps={{
                paper: {
                  sx: {
                    background: isDarkMode
                      ? "linear-gradient(to right, #1f1c2c, #928dab)"
                      : "linear-gradient(279deg,rgba(208, 184, 227, 1) 0%, rgba(232, 232, 237, 1) 43%)",

                    color: isDarkMode ? "white" : "black",
                    borderRadius: "12px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
                    "& .MuiAutocomplete-option": {
                      padding: "10px 16px",
                      "&[aria-selected=true]": {
                        backgroundColor: "#8e24aa",
                      },
                    },
                  },
                },
              }}
            />
          )}

          <Button
            type="submit"
            variant="contained"
            startIcon={
              sending ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
            disabled={sending || !pushNotificationService || !clients?.length}
            sx={{
              display: "flex",
              padding: "10px 30px 10px 35px",
              margin: "20px auto",
              backgroundColor: "#8174A0",
              color: "#fff",
              fontSize: "0.9em",

              borderRadius: "4px",
              justifyContent: "center",
              "&:hover": {
                backgroundColor: "#8566AA",
              },
              "&:disabled": {
                backgroundColor: "#CCCCCC",
              },
            }}
          >
            {sending
              ? "Відправка..."
              : t("resources.PushNotificationService.source.send")}
          </Button>
        </form>
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: snackbar.vertical,
          horizontal: snackbar.horizontal,
        }}
        // autoHideDuration={3000}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default SendNotification;
